import dayjs from 'dayjs'
import { Form, Formik, useFormik } from 'formik'
import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import * as Yup from 'yup'
import api from '../../../api'
import Responsive from '../../../assets/config/Responsive'
import Colors from '../../../assets/styles/Colors'
import Loader from '../../../components/Loader'
import ProgressBar from '../../../components/ProgressBar'
import ScreenHeader from '../../../components/ScreenHeader'
import BirthDayInput from '../../../components/v2/BirthDayInput'
import CustomLink from '../../../components/v2/CustomLink'
import Dropdown from '../../../components/v2/Dropdown'
import GenderCard from '../../../components/v2/GenderCard'
import Input from '../../../components/v2/Input'
import Message from '../../../components/v2/Message'
import { useAppSelector } from '../../../store'
import { AppState } from '../../../store/appSlice'
import { NewUserState, setNewUserField, setNewUserObj } from '../../../store/newUser'
import PhoneNumberInput from '../../../components/v2/PhoneNumberInput'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
//
const SignUpPersonalInfo = () => {
  //
  const dispatch = useDispatch()
  //
  const { t } = useTranslation()
  //
  const navigate = useNavigate()
  //
  const { lang, isProduction } = useAppSelector((root: { app: AppState }) => root.app)
  //
  const { data: countries, isLoading: isCountriesLoading } = api.useGetCountriesQuery(undefined)
  //
  const { personal } = useAppSelector((data: { newUser: NewUserState }) => data.newUser)
  //
  const [sgsRequest, { isLoading: isSgsRequestLoading, data: sgsResult, isError: isSgsResultError }] =
    api.useSgsRequestMutation()
  //
  //
  // Convert the input date string to a JavaScript Date object
  const dateParts = (personal.birthday && (personal.birthday as unknown as string))
    ?.split('/')
    .map((part) => part.trim())
  const formattedDate = dateParts && new Date(`${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`)
  // Format the date to "YYYY-MM-DD"
  const formattedDateString =
    (personal.birthday &&
      personal.birthday.length === 14 &&
      formattedDate &&
      formattedDate?.toISOString()?.split('T')[0]) ||
    null
  //
  //
  const buyerAge = dayjs(new Date()).diff(dayjs(formattedDateString), 'year')
  //
  // ###
  // SGS & set user info from SGS & if user not logged
  const requestSgsAndSetResponseData = useCallback(async () => {
    if (
      personal.sgsVerificationSended === false &&
      personal.citizenship &&
      personal.citizenship === 'GE' &&
      personal.personalNumber &&
      personal.personalNumber.length === 11 &&
      personal.birthday &&
      buyerAge >= 18 &&
      formattedDateString
    ) {
      dispatch(
        setNewUserField({
          fieldName: 'sgsVerificationSended',
          fieldValue: true,
        }),
      )
      try {
        await sgsRequest({
          birthday: formattedDateString,
          personalNumber: personal.personalNumber,
        }).unwrap()
      } catch (error: any) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0]
          const errorMessage = firstError.message
          toast.error(errorMessage)
        } else {
          toast.error('Მოხდა შეცდომა. Გთხოვთ სცადოთ მოგვიანებით... (requestSgsAndSetResponseData)')
        }
      }
    }
  }, [personal, buyerAge, sgsRequest, formattedDateString, dispatch])
  //
  // send SGS request
  useEffect(() => {
    requestSgsAndSetResponseData()
    //
    return () => {}
  }, [requestSgsAndSetResponseData])
  // ###
  //
  //
  //
  // ###
  const setSgsUserDataCallBack = useCallback(() => {
    // set sgs response data
    if (
      isProduction &&
      sgsResult &&
      sgsResult.sgsStatusText &&
      sgsResult.sgsStatusText.toLowerCase() === 'ok' &&
      personal.sgsVerificationSended === true &&
      personal.sgsVerification === false
    ) {
      //
      return [
        dispatch(
          setNewUserObj({
            phone: personal.phone,
            personalNumber: personal.personalNumber,
            citizenship: personal.citizenship,
            birthday: personal.birthday,
            gender: sgsResult.gender,
            sgsVerification: true,
            sgsVerificationSended: true,
            firstNameGe: sgsResult.translations?.ge?.firstName,
            lastNameGe: sgsResult.translations?.ge?.lastName,
            firstName: sgsResult.translations?.en?.firstName || '',
            lastName: sgsResult.translations?.en?.lastName || '',
          }),
        ),
      ]
    }
    //
  }, [sgsResult, personal, isProduction, dispatch])
  //
  //
  useEffect(() => {
    setSgsUserDataCallBack()
    //
    return () => {}
  }, [setSgsUserDataCallBack])
  // ###
  //
  //
  // english letter regex
  const englishLetterRegex = /^[A-Za-z]+$/
  // georgian letter regex
  const georgianLetterRegex = /^[\u10D0-\u10FF]*$/
  //
  //
  // yup validations schema
  const validationSchema = Yup.object({
    citizenship: Yup.string().required(`${t('required')}`),

    firstName: Yup.string()
      .min(2, `${t('minNameLetterLenght')}`)
      .matches(englishLetterRegex, `${t('onlyEnglishLetters')}`)
      .required(`${t('required')}`),

    lastName: Yup.string()
      .min(2, `${t('minNameLetterLenght')}`)
      .matches(englishLetterRegex, `${t('onlyEnglishLetters')}`)
      .required(`${t('required')}`),

    firstNameGe: Yup.string().when('citizenship', {
      is: (citizenship: string) => citizenship === 'GE',
      then: Yup.string()
        .min(2, `${t('minNameLetterLenght')}`)
        .matches(georgianLetterRegex, `${t('onlyGeorgianLetters')}`)
        .required(`${t('required')}`),
      otherwise: Yup.string().notRequired(),
    }),

    lastNameGe: Yup.string().when('citizenship', {
      is: (citizenship: string) => citizenship === 'GE',
      then: Yup.string()
        .min(2, `${t('minNameLetterLenght')}`)
        .matches(georgianLetterRegex, `${t('onlyGeorgianLetters')}`)
        .required(`${t('required')}`),
      otherwise: Yup.string().notRequired(),
    }),

    personalNumber: Yup.string().when('citizenship', {
      is: (citizenship: string) => citizenship === 'GE',
      then: Yup.string()
        .matches(/^\S*$/, `${t('spaceValidation')}`)
        .matches(/^[a-zA-Z0-9]*$/, ` `)
        .min(11, `${t('personalNumberValidation')}`)
        .max(11, `${t('personalNumberValidation')}`)
        .required(`${t('required')}`),
      otherwise: Yup.string()
        .matches(/^[0-9]+$/, ` `)
        .notRequired(),
    }),

    passportNumber: Yup.string().when('citizenship', {
      is: (citizenship: string) => citizenship !== 'GE',
      then: Yup.string()
        .matches(/^\S*$/, `${t('spaceValidation')}`)
        .matches(/^[a-zA-Z0-9]*$/, ` `)
        // .max(9, `${t('passportNumberValidation')}`)
        .required(`${t('required')}`),
      otherwise: Yup.string()
        .matches(/^\S*$/, `${t('spaceValidation')}`)
        .matches(/^[a-zA-Z0-9]*$/, ` `)
        .max(9, `${t('passportNumberValidation')}`),
    }),

    phone: Yup.string().when('citizenship', {
      is: (citizenship: string) => citizenship === 'GE',
      then: Yup.string()
        .matches(/^\S*$/, `${t('spaceValidation')}`)
        // .min(9, `${t('enterValidPhone')}`)
        // .max(9, `${t('enterValidPhone')}`)
        .required(`${t('required')}`),
      otherwise: Yup.string().notRequired(),
    }),

    email: Yup.string()
      .matches(/^\S*$/, `${t('spaceValidation')}`)
      .email(`${t('enterValidMail')}`)
      .required(`${t('required')}`),

    gender: Yup.string()
      .matches(/^\S*$/, `${t('spaceValidation')}`)
      .required(`${t('required')}`),
    birthday: Yup.string().required(`${t('required')}`),
  })
  //
  // initial values
  const initialValues = {
    citizenship: personal?.citizenship || '',
    personalNumber: personal?.personalNumber || '',
    birthday: personal?.birthday || '',
    passportNumber: personal?.passportNumber || '',
    firstNameGe: personal?.firstNameGe || '',
    lastNameGe: personal?.lastNameGe || '',
    firstName: personal?.firstName || '',
    lastName: personal?.lastName || '',
    phone: personal?.phone || '',
    email: personal?.email || '',
    gender: personal?.gender || '',
  }
  //
  // formi state
  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(false)
    },
  })
  //
  const handleNextButtonClick = (setTouched: any) => {
    setTouched({
      citizenship: true,
      personalNumber: true,
      birthday: true,
      passportNumber: true,
      firstNameGe: true,
      lastNameGe: true,
      firstName: true,
      lastName: true,
      phone: true,
      email: true,
      gender: true,
    })

    formik.validateForm().then((errors) => {
      if (Object.keys(errors).length === 0) {
        navigate('/auth/create-password')
      }
    })
  }
  //
  //
  const showInputsBoolean = true
  //
  //
  //
  const MainLoading = isCountriesLoading || isSgsRequestLoading
  //
  return MainLoading ? (
    <Loader />
  ) : (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize={true}
      validateOnMount={true}
      validateOnChange={true}
      validateOnBlur={true}
      onSubmit={(values, { setSubmitting }) => {
        setSubmitting(false)
      }}
    >
      {({ handleSubmit, handleBlur, setFieldValue, touched, errors, setTouched }) => (
        <Form onSubmit={handleSubmit}>
          <Wrapper>
            <Helmet>
              <title>{t('personalInfoHelmetTitle')}</title>
              <meta name="description" content={`${t('personalInfoHelmetTitle')}`} />
              <link rel="canonical" href={window.location.href} />
            </Helmet>
            <ScreenHeader text={`${t('homePage')}`} url="/" />
            <ProgressBar nextButton={false} prevButton={false} stepNumber={1} totalCount={2} filledCount={1} />
            <Block>
              <LargeTitle>{t('enterYourInformation')}</LargeTitle>
              <Form>
                <Content>
                  <InfoBlock>
                    {countries && (
                      <Dropdown
                        title={`${t('cityzenship')}`}
                        isSearchable={true}
                        isDisabled={
                          (personal.sgsVerification === true && personal.citizenship) || isSgsRequestLoading
                            ? true
                            : false
                        }
                        value={countries.find((x) => x.isoCode === personal.citizenship)?.isoCode}
                        options={[
                          ...countries.map((c) => ({
                            value: c.isoCode,
                            label: c.translations?.[lang]?.name,
                          })),
                        ]}
                        onChange={(event) =>
                          dispatch(
                            setNewUserField({
                              fieldName: 'citizenship',
                              fieldValue: event.value,
                            }),
                          )
                        }
                        error={(touched.citizenship || personal.citizenship) && errors.citizenship ? true : false}
                        errorText={errors.citizenship}
                      />
                    )}

                    <Input
                      id="personalNumber"
                      label={`${t('personalNumber')}`}
                      type="text"
                      placeholder=""
                      disabled={
                        (personal.sgsVerification === true && personal.personalNumber) || isSgsRequestLoading
                          ? true
                          : false
                      }
                      value={personal.personalNumber || ''}
                      onChange={(value) => [
                        dispatch(
                          setNewUserField({
                            fieldName: 'personalNumber',
                            fieldValue: value,
                          }),
                        ),
                        setFieldValue('personalNumber', value),
                      ]}
                      width={'fullWidth'}
                      error={
                        (touched.personalNumber || personal.personalNumber) && errors.personalNumber ? true : false
                      }
                      errorText={errors.personalNumber}
                      name="personalNumber"
                      onBlur={handleBlur}
                    />

                    <BirthDayInput
                      width={'fullWidth'}
                      disabled={
                        (personal.sgsVerification === true && personal.birthday) || isSgsRequestLoading ? true : false
                      }
                      id="date-of-birth"
                      label={t('dateOfBirth')}
                      placeholder={t('dayMonthYear')}
                      // @ts-ignore
                      value={personal.birthday || ''}
                      onChange={(value: string) => {
                        return [
                          dispatch(
                            setNewUserField({
                              fieldName: 'birthday',
                              fieldValue: value,
                            }),
                          ),
                          setFieldValue('birthday', value),
                        ]
                      }}
                      error={(touched.birthday || personal.birthday) && errors.birthday ? true : false}
                      errorText={errors.birthday}
                    />

                    {personal.birthday && personal.birthday.length === 14 && buyerAge <= 17 ? (
                      <>
                        <Message variant="light-red" text={`${t('ageIsNotValidRegistration')}`} />
                        <br />
                      </>
                    ) : null}

                    {showInputsBoolean && (
                      <Input
                        id="passportNumber"
                        label={`${t('passportNumber')}`}
                        type="text"
                        placeholder=""
                        value={personal.passportNumber || ''}
                        onChange={(value) => [
                          dispatch(
                            setNewUserField({
                              fieldName: 'passportNumber',
                              fieldValue: value,
                            }),
                          ),
                          setFieldValue('passportNumber', value),
                        ]}
                        width={'fullWidth'}
                        error={
                          (touched.passportNumber || personal.passportNumber) && errors.passportNumber ? true : false
                        }
                        errorText={errors.passportNumber}
                        name="passportNumber"
                        onBlur={handleBlur}
                      />
                    )}

                    {showInputsBoolean && (
                      <>
                        {personal.citizenship === 'GE' && (
                          <>
                            <Input
                              id="firstNameGe"
                              label={`${t('nameInGeorgia')}`}
                              type="text"
                              disabled={
                                (personal.sgsVerification === true && personal.firstNameGe) || isSgsRequestLoading
                                  ? true
                                  : false
                              }
                              placeholder=""
                              value={personal?.firstNameGe || ''}
                              onChange={(value) => [
                                dispatch(
                                  setNewUserField({
                                    fieldName: 'firstNameGe',
                                    fieldValue: value,
                                  }),
                                ),
                                setFieldValue('firstNameGe', value),
                              ]}
                              width={'fullWidth'}
                              error={(touched.firstNameGe || personal.firstNameGe) && errors.firstNameGe ? true : false}
                              errorText={errors.firstNameGe}
                              name="firstNameGe"
                              onBlur={handleBlur}
                            />

                            <Input
                              id="lastNameGe"
                              label={`${t('surnameInGeorgia')}`}
                              type="text"
                              disabled={
                                (personal.sgsVerification === true && personal.lastNameGe) || isSgsRequestLoading
                                  ? true
                                  : false
                              }
                              placeholder=""
                              value={personal.lastNameGe || ''}
                              onChange={(value) => [
                                dispatch(
                                  setNewUserField({
                                    fieldName: 'lastNameGe',
                                    fieldValue: value,
                                  }),
                                ),
                                setFieldValue('lastNameGe', value),
                              ]}
                              width={'fullWidth'}
                              error={(touched.lastNameGe || personal.lastNameGe) && errors.lastNameGe ? true : false}
                              errorText={errors.lastNameGe}
                              name="lastNameGe"
                              onBlur={handleBlur}
                            />
                          </>
                        )}
                      </>
                    )}

                    {showInputsBoolean && (
                      <Input
                        id="firstName"
                        label={`${t('nameInEnglish')}`}
                        type="text"
                        placeholder=""
                        value={personal?.firstName || ''}
                        onChange={(value) => [
                          dispatch(
                            setNewUserField({
                              fieldName: 'firstName',
                              fieldValue: value,
                            }),
                          ),
                          setFieldValue('firstName', value),
                        ]}
                        width={'fullWidth'}
                        error={(touched.firstName || personal.firstName) && errors.firstName ? true : false}
                        errorText={errors.firstName}
                        name="firstName"
                        onBlur={handleBlur}
                      />
                    )}

                    {showInputsBoolean && (
                      <Input
                        id="lastName"
                        label={`${t('surnameInEnglish')}`}
                        type="text"
                        placeholder=""
                        value={personal.lastName || ''}
                        onChange={(value) => [
                          dispatch(
                            setNewUserField({
                              fieldName: 'lastName',
                              fieldValue: value,
                            }),
                          ),
                          setFieldValue('lastName', value),
                        ]}
                        width={'fullWidth'}
                        error={(touched.lastName || personal.lastName) && errors.lastName ? true : false}
                        errorText={errors.lastName}
                        name="lastName"
                        onBlur={handleBlur}
                      />
                    )}

                    {showInputsBoolean && (
                      <PhoneNumberInput
                        id="phone"
                        hideButtons={true}
                        label={`${t('phone')}`}
                        value={personal.phone || ''}
                        onChange={(value) => [
                          dispatch(
                            setNewUserField({
                              fieldName: 'phone',
                              fieldValue: value,
                            }),
                          ),
                          setFieldValue('phone', value),
                        ]}
                        error={(touched.phone || personal.phone) && errors.phone ? true : false}
                        errorText={errors.phone}
                        onBlur={handleBlur}
                      />
                    )}

                    {showInputsBoolean && (
                      <Input
                        id="email"
                        label={`${t('email')}`}
                        type="text"
                        placeholder=""
                        value={personal.email || ''}
                        onChange={(value) => [
                          dispatch(
                            setNewUserField({
                              fieldName: 'email',
                              fieldValue: value,
                            }),
                          ),
                          setFieldValue('email', value),
                        ]}
                        width={'fullWidth'}
                        error={(touched.email || personal.email) && errors.email ? true : false}
                        errorText={errors.email}
                        name="email"
                        onBlur={handleBlur}
                      />
                    )}

                    {showInputsBoolean && (
                      <div>
                        <ChooseGenderLabel>{t('chooseGender')}</ChooseGenderLabel>
                        <GenderBlock>
                          {[
                            {
                              gender: 'female',
                              name: `${t('female')}`,
                            },
                            {
                              gender: 'male',
                              name: `${t('male')}`,
                            },
                          ].map((item, index) => (
                            <GenderCard
                              key={index}
                              pet={false}
                              selected={personal?.gender === item?.gender}
                              gender={item.gender}
                              femaleText={item.name}
                              maleText={item.name}
                              onClick={() => [
                                dispatch(
                                  setNewUserField({
                                    fieldName: 'gender',
                                    fieldValue: item.gender,
                                  }),
                                ),
                                setFieldValue('gender', item.gender),
                              ]}
                              error={(touched.gender || personal.gender) && errors.gender ? true : false}
                              errorText={errors.gender}
                              onBlur={handleBlur}
                            />
                          ))}
                        </GenderBlock>
                      </div>
                    )}
                  </InfoBlock>

                  <NavigateButtons>
                    <div className="d-flex justify-content-center align-items-center mb-1">
                      <CustomLink
                        onClick={() => handleNextButtonClick(setTouched)}
                        size={'medium'}
                        rightLineIcon={true}
                        text={`${t('next')}`}
                      />
                    </div>
                  </NavigateButtons>
                </Content>
              </Form>
            </Block>
          </Wrapper>
        </Form>
      )}
    </Formik>
  )
}
//
const ChooseGenderLabel = styled.label`
  font-size: 14px;
  margin-bottom: 10px;
  color: ${Colors.primaryGrey};
`
const GenderBlock = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: 1fr;
  grid-column-gap: 20px;
  grid-row-gap: 0px;
`
const NavigateButtons = styled.div`
  position: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  left: 0;
  bottom: 22px;
  z-index: 2;
  height: 24px;
  ${Responsive.tablet} {
    bottom: 12px;
  }
  ${Responsive.mobile} {
    bottom: 12px;
  }
`
const InfoBlock = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  grid-column-gap: 0px;
  grid-row-gap: 20px;
  margin-top: 20px;

  padding: 50px 64px;

  width: 100%;

  /* h3 title */
  h3 {
    font-size: 18px;
    color: ${Colors.Black};
    text-align: center;
    text-transform: capitalize;
  }

  /* p descirption */
  p {
    font-size: 14px;
    line-height: 22px;
    color: ${Colors.SilverChalice};
    text-align: center;
    margin-bottom: 45px;
  }

  ${Responsive.mobile} {
    padding: 32px 24px;
    /* h3 title */
    h3 {
      font-size: 20px;
      margin-bottom: 15px;
    }

    /* p descirption */
    p {
      margin-bottom: 27px;
    }
  }
`

const LargeTitle = styled.h1`
  font-size: 36px;
  line-height: 40px;
  color: ${Colors.primaryBlack};
  text-align: center;
  margin-top: 56px;
  margin-bottom: 56px;
  text-transform: capitalize;

  /* responsive */
  ${Responsive.tablet} {
    font-size: 24px;
    line-height: 26px;
  }

  /* responsive */
  ${Responsive.mobile} {
    font-size: 18px;
    line-height: 20px;
    margin-bottom: 56px;
    margin-top: 56px;
  }
`
const Content = styled.div`
  background-color: ${Colors.white};
  max-width: 528px;
  width: 100%;
  border-radius: 8px;
  margin-top: 26px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 40px;

  /* box shadow */
  box-shadow: 0px 0px 7px -1px rgba(0, 0, 0, 0.02);
  -webkit-box-shadow: 0px 0px 7px -1px rgba(0, 0, 0, 0.02);
  -moz-box-shadow: 0px 0px 7px -1px rgba(0, 0, 0, 0.02);
  &:hover {
    box-shadow: 0px 0px 15px 1px rgba(0, 0, 0, 0.02);
    -webkit-box-shadow: 0px 0px 15px 1px rgba(0, 0, 0, 0.02);
    -moz-box-shadow: 0px 0px 15px 1px rgba(0, 0, 0, 0.02);
  }

  /* responsive */
  ${Responsive.tablet} {
  }
  ${Responsive.mobile} {
    width: 100%;
    padding-block: 20px;
  }
`
const Block = styled.div`
  ${Responsive.mobile} {
    padding-inline: 20px;
  }
`
const Wrapper = styled.div`
  padding-bottom: 64px;
  margin-bottom: 64px;
  background-color: ${Colors.backgroundGrey};
  min-height: 100vh;
  ${Responsive.tablet} {
    padding-bottom: 80px;
    margin-bottom: 0px;
  }
  ${Responsive.mobile} {
    padding-bottom: 80px;
    margin-bottom: 0px;
  }
`
//
export default SignUpPersonalInfo
